<template>
  <div class="ul-root">

    <user-list-add-new
      :is-add-new-user-sidebar-active.sync="isAddNewUserSidebarActive"
      :editing-user="editingUser"
      :role-options="roleOptions"
      :plan-options="planOptions"
      @refetch-data="onSaveUser"
      @close="onSidebarClose"
    />

    <!-- Page Header -->
    <div class="ul-page-header">
      <div>
        <h2 class="ul-page-title">Usuários</h2>
        <p class="ul-page-sub">Gerencie os usuários da plataforma</p>
      </div>
      <button class="ul-btn-add" @click="openAddUser">
        <span class="ul-btn-icon">+</span>
        Adicionar Usuário
      </button>
    </div>

    <!-- Stats Row -->
    <div class="ul-stats-row">
      <div class="ul-stat-card ul-stat-total">
        <div class="ul-stat-icon">
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
        <div>
          <div class="ul-stat-num">{{ totalUsers }}</div>
          <div class="ul-stat-label">Total de usuários</div>
        </div>
      </div>
      <div class="ul-stat-card ul-stat-active" @click="setStatusFilter('1')" :class="{ 'ul-stat-active-sel': statusFilter === '1' }">
        <div class="ul-stat-icon">
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M22 4L12 14.01l-3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <div>
          <div class="ul-stat-num">{{ statsAtivos }}</div>
          <div class="ul-stat-label">Ativos</div>
        </div>
      </div>
      <div class="ul-stat-card ul-stat-inactive" @click="setStatusFilter('0')" :class="{ 'ul-stat-inactive-sel': statusFilter === '0' }">
        <div class="ul-stat-icon">
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
        <div>
          <div class="ul-stat-num">{{ statsInativos }}</div>
          <div class="ul-stat-label">Inativos</div>
        </div>
      </div>
      <div class="ul-stat-card ul-stat-admin" @click="setRoleFilter('admin')" :class="{ 'ul-stat-admin-sel': roleFilter === 'admin' }">
        <div class="ul-stat-icon">
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
        </div>
        <div>
          <div class="ul-stat-num">{{ statsAdmins }}</div>
          <div class="ul-stat-label">Admins</div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="ul-card">

      <!-- Filters Bar -->
      <div class="ul-filters-bar">
        <div class="ul-filters-left">
          <!-- Status Pills -->
          <div class="ul-filter-group">
            <span class="ul-filter-label">Status</span>
            <div class="ul-pills">
              <button class="ul-pill" :class="{ active: !statusFilter }" @click="statusFilter = null">Todos</button>
              <button class="ul-pill ul-pill-success" :class="{ active: statusFilter === '1' }" @click="setStatusFilter('1')">Ativo</button>
              <button class="ul-pill ul-pill-danger" :class="{ active: statusFilter === '0' }" @click="setStatusFilter('0')">Inativo</button>
            </div>
          </div>
          <!-- Role Filter -->
          <div class="ul-filter-group">
            <span class="ul-filter-label">Cargo</span>
            <div class="ul-pills">
              <button class="ul-pill" :class="{ active: !roleFilter }" @click="roleFilter = null">Todos</button>
              <button class="ul-pill ul-pill-warning" :class="{ active: roleFilter === 'admin' }" @click="setRoleFilter('admin')">Admin</button>
              <button class="ul-pill ul-pill-info" :class="{ active: roleFilter === 'client' }" @click="setRoleFilter('client')">Cliente</button>
            </div>
          </div>
          <!-- Date Range -->
          <div class="ul-filter-group ul-filter-date">
            <span class="ul-filter-label">Período</span>
            <div class="ul-date-range">
              <input type="date" v-model="dateStart" class="ul-date-input" placeholder="Início" />
              <span class="ul-date-sep">→</span>
              <input type="date" v-model="dateEnd" class="ul-date-input" placeholder="Fim" />
              <button v-if="dateStart || dateEnd" class="ul-date-clear" @click="clearDates" title="Limpar">✕</button>
            </div>
          </div>
        </div>

        <div class="ul-filters-right">
          <div class="ul-search-wrap">
            <svg class="ul-search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            <input v-model="searchQuery" class="ul-search-input" placeholder="Pesquisar por nome, email…" />
            <button v-if="searchQuery" class="ul-search-clear" @click="searchQuery = ''">✕</button>
          </div>
          <div class="ul-per-page">
            <span class="ul-filter-label">Exibir</span>
            <select v-model="perPage" class="ul-select-rows">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
          </div>
          <button v-if="hasAnyFilter" class="ul-btn-clear-all" @click="clearAllFilters">
            Limpar filtros
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="ul-loading">
        <div class="ul-spinner"></div>
        <span>Carregando usuários…</span>
      </div>

      <!-- Table -->
      <div v-else class="ul-table-wrap">
        <table class="ul-table">
          <thead>
            <tr>
              <th class="ul-th-sortable" @click="sortColumn('user')">
                USUÁRIO
                <span class="ul-sort-icon">{{ getSortIcon('user') }}</span>
              </th>
              <th class="ul-th-sortable" @click="sortColumn('email')">
                EMAIL
                <span class="ul-sort-icon">{{ getSortIcon('email') }}</span>
              </th>
              <th>SITE</th>
              <th>CARGO</th>
              <th>PLANO</th>
              <th class="ul-th-sortable" @click="sortColumn('status')">
                STATUS
                <span class="ul-sort-icon">{{ getSortIcon('status') }}</span>
              </th>
              <th class="ul-th-sortable" @click="sortColumn('created_at')">
                CADASTRO
                <span class="ul-sort-icon">{{ getSortIcon('created_at') }}</span>
              </th>
              <th class="ul-th-actions">AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!currentItems.length">
              <td colspan="8" class="ul-empty">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" stroke-width="1.5"/></svg>
                <p>Nenhum usuário encontrado</p>
              </td>
            </tr>
            <tr v-for="user in currentItems" :key="user.id" class="ul-tr">
              <!-- User -->
              <td class="ul-td-user">
                <div class="ul-user-cell">
                  <div class="ul-avatar" :style="{ background: getAvatarColor(user.fullName) }">
                    <img v-if="user.avatar" :src="user.avatar" :alt="user.fullName" class="ul-avatar-img" />
                    <span v-else>{{ getInitials(user.fullName) }}</span>
                  </div>
                  <div class="ul-user-info">
                    <router-link :to="{ name: 'apps-users-view', params: { id: user.id } }" class="ul-user-name">
                      {{ user.fullName }}
                    </router-link>
                    <span class="ul-user-handle">@{{ user.username }}</span>
                  </div>
                </div>
              </td>
              <!-- Email -->
              <td class="ul-td-email">
                <span class="ul-email">{{ user.email }}</span>
              </td>
              <!-- Site -->
              <td class="ul-td-site">
                <a v-if="user.siteUrl" :href="user.siteUrl.startsWith('http') ? user.siteUrl : 'https://' + user.siteUrl" target="_blank" rel="noopener" class="ul-site-link" :title="user.siteUrl">
                  {{ user.siteUrl.replace(/^https?:\/\//, '') }}
                </a>
                <span v-else class="ul-site-empty">—</span>
              </td>
              <!-- Role -->
              <td>
                <span class="ul-role-badge" :class="'ul-role-' + (user.role || 'client')">
                  <svg v-if="user.role === 'admin'" width="12" height="12" fill="none" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="currentColor" stroke-width="2"/></svg>
                  <svg v-else width="12" height="12" fill="none" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/></svg>
                  {{ user.role === 'admin' ? 'Admin' : 'Cliente' }}
                </span>
              </td>
              <!-- Plan -->
              <td>
                <span class="ul-plan-badge">{{ user.currentPlan || '—' }}</span>
              </td>
              <!-- Status -->
              <td>
                <span class="ul-status-badge" :class="user.status === 'Ativo' ? 'ul-status-ativo' : 'ul-status-inativo'">
                  <span class="ul-status-dot"></span>
                  {{ user.status }}
                </span>
              </td>
              <!-- Date -->
              <td class="ul-td-date">{{ formatDate(user.created_at) }}</td>
              <!-- Actions -->
              <td class="ul-td-actions">
                <div class="ul-actions">
                  <router-link :to="{ name: 'apps-users-view', params: { id: user.id } }" class="ul-action-btn ul-action-view" title="Ver detalhes">
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
                  </router-link>
                  <button class="ul-action-btn ul-action-edit" title="Editar" @click="openEditUser(user)">
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                  </button>
                  <button class="ul-action-btn ul-action-delete" title="Deletar" @click="confirmDelete(user)">
                    <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer: Pagination -->
      <div class="ul-footer">
        <span class="ul-footer-info">
          Mostrando <strong>{{ dataMeta.from }}</strong> a <strong>{{ dataMeta.to }}</strong> de <strong>{{ dataMeta.of }}</strong> usuários
        </span>
        <div class="ul-pagination">
          <button class="ul-page-btn" :disabled="currentPage === 1" @click="currentPage--">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <button
            v-for="page in pagesArray"
            :key="page"
            class="ul-page-btn"
            :class="{ 'ul-page-active': page === currentPage, 'ul-page-ellipsis': page === '…' }"
            :disabled="page === '…'"
            @click="page !== '…' && (currentPage = page)"
          >{{ page }}</button>
          <button class="ul-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteTarget" class="ul-modal-overlay" @click.self="deleteTarget = null">
      <div class="ul-modal ul-modal-delete">
        <div class="ul-modal-icon ul-modal-icon-danger">
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
        <h3 class="ul-modal-title">Excluir usuário?</h3>
        <p class="ul-modal-text">
          Tem certeza que deseja excluir <strong>{{ deleteTarget.fullName }}</strong>?
          <br>Esta ação não pode ser desfeita.
        </p>
        <div class="ul-modal-actions">
          <button class="ul-btn-secondary" @click="deleteTarget = null">Cancelar</button>
          <button class="ul-btn-danger" @click="executeDelete">
            <span v-if="deleting" class="ul-spinner-sm"></span>
            <span v-else>Excluir usuário</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import store from '@/store'
import { ref, computed, watch, onUnmounted } from '@vue/composition-api'
import UserListAddNew from '@/views/App/Usuarios/usuarios-lista/UserListAddNew.vue'
import userStoreModule from '@/views/App/Usuarios/userStoreModule'

export default {
  components: { UserListAddNew },

  setup() {
    const USER_APP_STORE_MODULE_NAME = 'app-user'
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
      store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(USER_APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewUserSidebarActive = ref(false)
    const editingUser = ref(null)
    const loading = ref(false)
    const deleting = ref(false)
    const deleteTarget = ref(null)
    const allUsers = ref([])

    const searchQuery = ref('')
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const roleFilter = ref(null)
    const planFilter = ref(null)
    const statusFilter = ref(null)
    const dateStart = ref(null)
    const dateEnd = ref(null)
    const perPage = ref(10)
    const currentPage = ref(1)
    const totalUsers = ref(0)

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Cliente', value: 'client' },
    ]
    const planOptions = [
      { label: 'Vsl-Booster', value: 'VSL Booster' },
      { label: 'Pro-Booster', value: 'PRO Booster' },
    ]

    const currentItems = ref([])

    const fetchUsers = () => {
      loading.value = true
      store.dispatch('app-user/fetchUsers', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
        dateStart: dateStart.value,
        dateEnd: dateEnd.value,
      }).then(response => {
        const { users, total } = response.data
        currentItems.value = users || []
        totalUsers.value = total || 0
      }).catch(() => {
        currentItems.value = []
      }).finally(() => {
        loading.value = false
      })
    }

    fetchUsers()

    watch([searchQuery, roleFilter, planFilter, statusFilter, dateStart, dateEnd, perPage, sortBy, isSortDirDesc], () => {
      currentPage.value = 1
      fetchUsers()
    })
    watch(currentPage, fetchUsers)

    // Stats computed from current data (approximation)
    const statsAtivos = computed(() => currentItems.value.filter(u => u.status === 'Ativo').length)
    const statsInativos = computed(() => currentItems.value.filter(u => u.status !== 'Ativo').length)
    const statsAdmins = computed(() => currentItems.value.filter(u => u.role === 'admin').length)

    const dataMeta = computed(() => {
      const from = perPage.value * (currentPage.value - 1) + (currentItems.value.length ? 1 : 0)
      const to = perPage.value * (currentPage.value - 1) + currentItems.value.length
      return { from, to, of: totalUsers.value }
    })

    const totalPages = computed(() => Math.ceil(totalUsers.value / perPage.value) || 1)

    const pagesArray = computed(() => {
      const total = totalPages.value
      const cur = currentPage.value
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = []
      if (cur <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('…', total)
      } else if (cur >= total - 3) {
        pages.push(1, '…')
        for (let i = total - 4; i <= total; i++) pages.push(i)
      } else {
        pages.push(1, '…', cur - 1, cur, cur + 1, '…', total)
      }
      return pages
    })

    const hasAnyFilter = computed(() => !!(statusFilter.value || roleFilter.value || dateStart.value || dateEnd.value || searchQuery.value))

    const clearAllFilters = () => {
      statusFilter.value = null
      roleFilter.value = null
      dateStart.value = null
      dateEnd.value = null
      searchQuery.value = ''
    }

    const clearDates = () => {
      dateStart.value = null
      dateEnd.value = null
    }

    const setStatusFilter = val => {
      statusFilter.value = statusFilter.value === val ? null : val
    }

    const setRoleFilter = val => {
      roleFilter.value = roleFilter.value === val ? null : val
    }

    const sortColumn = col => {
      if (sortBy.value === col) {
        isSortDirDesc.value = !isSortDirDesc.value
      } else {
        sortBy.value = col
        isSortDirDesc.value = false
      }
    }

    const getSortIcon = col => {
      if (sortBy.value !== col) return '↕'
      return isSortDirDesc.value ? '↓' : '↑'
    }

    const getInitials = name => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase()
    }

    const AVATAR_COLORS = [
      '#E86A10', '#0D3349', '#F5B935', '#3A86FF', '#8338EC',
      '#06D6A0', '#EF476F', '#118AB2', '#073B4C', '#FFB703',
    ]
    const getAvatarColor = name => {
      if (!name) return AVATAR_COLORS[0]
      const idx = name.charCodeAt(0) % AVATAR_COLORS.length
      return AVATAR_COLORS[idx]
    }

    const formatDate = val => {
      if (!val) return '—'
      const d = new Date(val)
      return d.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      }).replace(',', '')
    }

    const openAddUser = () => {
      editingUser.value = null
      isAddNewUserSidebarActive.value = true
    }

    const openEditUser = user => {
      editingUser.value = user
      isAddNewUserSidebarActive.value = true
    }

    const onSidebarClose = () => {
      editingUser.value = null
    }

    const onSaveUser = () => {
      editingUser.value = null
      fetchUsers()
    }

    const confirmDelete = user => {
      deleteTarget.value = user
    }

    const executeDelete = async () => {
      deleting.value = true
      try {
        // Tenta deletar arquivos no S3 (ignora erro de CORS em dev)
        if (deleteTarget.value.fileKey) {
          try { await store.dispatch('app-user/deleteUserAws', deleteTarget.value.fileKey) } catch (e) { console.warn('[delete] AWS cleanup falhou (CORS em dev):', e.message) }
        }
        // Deleta usuario e todos os dados no banco
        await store.dispatch('app-user/deleteUser', deleteTarget.value.id)
        deleteTarget.value = null
        fetchUsers()
      } catch (e) {
        console.error('[delete] Erro:', e)
        alert('Erro ao excluir: ' + (e.response?.data?.error || e.message))
      } finally {
        deleting.value = false
      }
    }

    return {
      isAddNewUserSidebarActive, editingUser, loading, deleting,
      deleteTarget, currentItems, totalUsers,
      searchQuery, sortBy, isSortDirDesc, roleFilter, planFilter,
      statusFilter, dateStart, dateEnd, perPage, currentPage,
      roleOptions, planOptions, dataMeta, totalPages, pagesArray,
      hasAnyFilter, statsAtivos, statsInativos, statsAdmins,
      clearAllFilters, clearDates, setStatusFilter, setRoleFilter,
      sortColumn, getSortIcon, getInitials, getAvatarColor, formatDate,
      openAddUser, openEditUser, onSidebarClose, onSaveUser,
      confirmDelete, executeDelete,
    }
  },
}
</script>

<style scoped>
/* ─── Root ─── */
.ul-root { padding: 0; }

/* ─── Page Header ─── */
.ul-page-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px;
}
.ul-page-title { font-size: 22px; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin: 0; }
.ul-page-sub { font-size: 13px; color: var(--dk-text-muted, #8a94a6); margin: 2px 0 0; }

/* ─── Add Button ─── */
.ul-btn-add {
  display: flex; align-items: center; gap: 8px;
  background: #E86A10; color: #fff; border: none;
  border-radius: 10px; padding: 10px 20px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  box-shadow: 0 4px 12px rgba(232,106,16,0.35);
}
.ul-btn-add:hover { background: #cf5e0d; transform: translateY(-1px); }
.ul-btn-icon { font-size: 20px; line-height: 1; margin-top: -1px; }

/* ─── Stats Row ─── */
.ul-stats-row {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 16px; margin-bottom: 20px;
}
.ul-stat-card {
  background: var(--dk-bg-card, #fff); border-radius: 14px; padding: 18px 20px;
  display: flex; align-items: center; gap: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5); cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
  box-shadow: var(--dk-shadow-sm, 0 2px 8px rgba(0,0,0,0.04));
}
.ul-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.09); }
.ul-stat-icon {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.ul-stat-total { cursor: default; }
.ul-stat-total .ul-stat-icon { background: rgba(13,51,73,0.08); color: var(--dk-text-primary, #0D3349); }
.ul-stat-active .ul-stat-icon { background: rgba(40,199,111,0.12); color: #28c76f; }
.ul-stat-inactive .ul-stat-icon { background: rgba(168,170,174,0.15); color: #82868b; }
.ul-stat-admin .ul-stat-icon { background: rgba(232,106,16,0.12); color: #E86A10; }
.ul-stat-active-sel { border-color: #28c76f !important; box-shadow: 0 0 0 3px rgba(40,199,111,0.15) !important; }
.ul-stat-inactive-sel { border-color: #82868b !important; box-shadow: 0 0 0 3px rgba(130,134,139,0.15) !important; }
.ul-stat-admin-sel { border-color: #E86A10 !important; box-shadow: 0 0 0 3px rgba(232,106,16,0.15) !important; }
.ul-stat-num { font-size: 26px; font-weight: 700; color: var(--dk-text-primary, #0D3349); line-height: 1; }
.ul-stat-label { font-size: 12px; color: var(--dk-text-muted, #8a94a6); margin-top: 3px; font-weight: 500; }

/* ─── Main Card ─── */
.ul-card {
  background: var(--dk-bg-card, #fff); border-radius: 16px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: var(--dk-shadow-md, 0 2px 12px rgba(0,0,0,0.05));
  overflow: hidden;
}

/* ─── Filters Bar ─── */
.ul-filters-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px; border-bottom: 1.5px solid var(--dk-border-light, #f5f6fa);
  flex-wrap: wrap; gap: 14px;
}
.ul-filters-left { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
.ul-filters-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.ul-filter-group { display: flex; flex-direction: column; gap: 6px; }
.ul-filter-label { font-size: 11px; font-weight: 600; color: var(--dk-text-muted, #8a94a6); text-transform: uppercase; letter-spacing: 0.05em; }

/* ─── Pills ─── */
.ul-pills { display: flex; gap: 6px; }
.ul-pill {
  padding: 4px 12px; border-radius: 20px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  background: transparent; font-size: 12px; font-weight: 600; cursor: pointer;
  color: var(--dk-text-secondary, #6e7d8f); transition: all 0.15s;
}
.ul-pill:hover { border-color: var(--dk-text-muted); background: var(--dk-bg-card-hover, #f8fafc); }
.ul-pill.active { background: #0D3349; color: #fff; border-color: #0D3349; }
.ul-pill-success.active { background: #28c76f; border-color: #28c76f; }
.ul-pill-danger.active { background: #ea5455; border-color: #ea5455; }
.ul-pill-warning.active { background: #E86A10; border-color: #E86A10; }
.ul-pill-info.active { background: #00cfe8; border-color: #00cfe8; }

/* ─── Date Range ─── */
.ul-filter-date { flex-direction: column; }
.ul-date-range { display: flex; align-items: center; gap: 6px; }
.ul-date-input {
  padding: 5px 8px; border-radius: 8px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  font-size: 12px; color: var(--dk-text-primary, #344563); background: var(--dk-bg-input, #f9fafb);
  width: 130px; transition: border-color 0.15s;
}
.ul-date-input:focus { outline: none; border-color: #E86A10; }
.ul-date-sep { font-size: 12px; color: var(--dk-text-faint, #aab0bc); font-weight: 500; }
.ul-date-clear {
  padding: 4px 8px; border: none; background: none;
  color: var(--dk-text-faint, #aab0bc); cursor: pointer; font-size: 13px;
  border-radius: 50%; transition: background 0.15s;
}
.ul-date-clear:hover { background: #fee2e2; color: #ea5455; }

/* ─── Search ─── */
.ul-search-wrap {
  position: relative; display: flex; align-items: center;
}
.ul-search-icon {
  position: absolute; left: 10px; color: var(--dk-text-faint, #aab0bc); pointer-events: none;
}
.ul-search-input {
  padding: 8px 32px 8px 32px; border-radius: 10px;
  border: 1.5px solid var(--dk-border-input, #e8ecf0); font-size: 13px; color: var(--dk-text-primary, #344563);
  background: var(--dk-bg-input, #f9fafb); width: 240px; transition: border-color 0.2s, box-shadow 0.2s;
}
.ul-search-input:focus { outline: none; border-color: #E86A10; box-shadow: 0 0 0 3px rgba(232,106,16,0.12); }
.ul-search-clear {
  position: absolute; right: 8px; background: none; border: none;
  color: var(--dk-text-faint, #aab0bc); cursor: pointer; font-size: 13px;
}
.ul-search-clear:hover { color: #ea5455; }

/* ─── Per Page ─── */
.ul-per-page { display: flex; align-items: center; gap: 8px; }
.ul-select-rows {
  padding: 6px 10px; border-radius: 8px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  font-size: 13px; color: var(--dk-text-primary, #344563); background: var(--dk-bg-input, #f9fafb);
  cursor: pointer; transition: border-color 0.15s;
}
.ul-select-rows:focus { outline: none; border-color: #E86A10; }

/* ─── Clear All ─── */
.ul-btn-clear-all {
  padding: 6px 14px; border-radius: 8px; border: 1.5px solid var(--dk-border-input, #e8ecf0);
  background: none; font-size: 12px; color: var(--dk-text-secondary, #6e7d8f); cursor: pointer;
  font-weight: 600; transition: all 0.15s; white-space: nowrap;
}
.ul-btn-clear-all:hover { border-color: #ea5455; color: #ea5455; background: #fff5f5; }

/* ─── Loading ─── */
.ul-loading {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 60px 0; gap: 14px;
  color: var(--dk-text-muted, #8a94a6); font-size: 14px;
}
.ul-spinner {
  width: 36px; height: 36px; border-radius: 50%;
  border: 3px solid var(--dk-border-light, #f0f2f5); border-top-color: #E86A10;
  animation: ul-spin 0.7s linear infinite;
}
@keyframes ul-spin { to { transform: rotate(360deg); } }

/* ─── Table ─── */
.ul-table-wrap { overflow-x: auto; }
.ul-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.ul-table thead tr { background: var(--dk-bg-inset, #f8f9fb); }
.ul-table th {
  padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700;
  color: var(--dk-text-muted, #8a94a6); letter-spacing: 0.07em; white-space: nowrap;
  border-bottom: 1.5px solid var(--dk-border-light, #f0f2f5); user-select: none;
}
.ul-th-sortable { cursor: pointer; }
.ul-th-sortable:hover { color: var(--dk-text-primary, #0D3349); }
.ul-sort-icon { font-size: 10px; margin-left: 4px; opacity: 0.6; }
.ul-th-actions { text-align: center; }
.ul-tr {
  border-bottom: 1px solid var(--dk-border-light, #f5f6fa); transition: background 0.12s;
}
.ul-tr:last-child { border-bottom: none; }
.ul-tr:hover { background: var(--dk-bg-card-hover, #fafbfc); }
.ul-table td { padding: 14px 16px; vertical-align: middle; }

/* User Cell */
.ul-td-user { min-width: 180px; }
.ul-user-cell { display: flex; align-items: center; gap: 12px; }
.ul-avatar {
  width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: #fff; overflow: hidden;
  position: relative;
}
.ul-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.ul-user-name {
  display: block; font-weight: 600; color: var(--dk-text-primary, #0D3349); text-decoration: none;
  font-size: 14px; line-height: 1.3; white-space: nowrap;
  transition: color 0.15s;
}
.ul-user-name:hover { color: #E86A10; }
.ul-user-handle { font-size: 12px; color: var(--dk-text-faint, #aab0bc); font-weight: 400; }

/* Email */
.ul-td-email { min-width: 180px; }
.ul-email { color: var(--dk-text-link, #5a6a7a); font-size: 13px; }

/* Site */
.ul-td-site { max-width: 180px; }
.ul-site-link {
  color: #E86A10; font-size: 13px; text-decoration: none;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
}
.ul-site-link:hover { text-decoration: underline; }
.ul-site-empty { color: var(--dk-text-faint, #aab0bc); font-size: 13px; }

/* Role Badge */
.ul-role-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600;
}
.ul-role-admin { background: rgba(232,106,16,0.1); color: #E86A10; }
.ul-role-client { background: rgba(0,207,232,0.1); color: #00a8ba; }

/* Plan Badge */
.ul-plan-badge {
  font-size: 12px; color: var(--dk-text-secondary, #6e7d8f); font-weight: 500;
  background: var(--dk-bg-inset, #f3f4f6); padding: 3px 9px; border-radius: 5px;
}

/* Status Badge */
.ul-status-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;
}
.ul-status-ativo { background: rgba(40,199,111,0.1); color: #28c76f; }
.ul-status-inativo { background: rgba(168,170,174,0.12); color: #82868b; }
.ul-status-dot {
  width: 7px; height: 7px; border-radius: 50%; background: currentColor;
}

/* Date */
.ul-td-date { color: var(--dk-text-muted, #8a94a6); font-size: 12.5px; white-space: nowrap; }

/* Actions */
.ul-td-actions { width: 110px; }
.ul-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }
.ul-action-btn {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s, color 0.15s, transform 0.1s;
  text-decoration: none; color: inherit; background: transparent;
}
.ul-action-btn:hover { transform: scale(1.1); }
.ul-action-view { color: #3A86FF; }
.ul-action-view:hover { background: rgba(58,134,255,0.1); }
.ul-action-edit { color: #E86A10; }
.ul-action-edit:hover { background: rgba(232,106,16,0.1); }
.ul-action-delete { color: #ea5455; }
.ul-action-delete:hover { background: rgba(234,84,85,0.1); }

/* Empty State */
.ul-empty {
  text-align: center; padding: 60px 20px;
  color: var(--dk-text-faint, #aab0bc);
}
.ul-empty svg { opacity: 0.4; margin-bottom: 12px; }
.ul-empty p { font-size: 14px; margin: 0; }

/* ─── Footer ─── */
.ul-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; border-top: 1.5px solid var(--dk-border-light, #f5f6fa);
  flex-wrap: wrap; gap: 12px;
}
.ul-footer-info { font-size: 13px; color: var(--dk-text-muted, #8a94a6); }
.ul-footer-info strong { color: var(--dk-text-primary, #344563); }

/* ─── Pagination ─── */
.ul-pagination { display: flex; align-items: center; gap: 4px; }
.ul-page-btn {
  min-width: 34px; height: 34px; padding: 0 6px; border-radius: 8px;
  border: 1.5px solid var(--dk-border-input, #e8ecf0); background: var(--dk-bg-card, #fff);
  font-size: 13px; font-weight: 600; color: var(--dk-text-secondary, #5a6a7a);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.ul-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.ul-page-btn:not(:disabled):hover { border-color: #E86A10; color: #E86A10; background: rgba(232,106,16,0.05); }
.ul-page-active { background: #E86A10 !important; border-color: #E86A10 !important; color: #fff !important; }
.ul-page-ellipsis { border-color: transparent; background: none; cursor: default; }

/* ─── Delete Modal ─── */
.ul-modal-overlay {
  position: fixed; inset: 0; background: rgba(13,51,73,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; backdrop-filter: blur(4px);
}
.ul-modal {
  background: var(--dk-bg-modal, #fff); border-radius: 20px; padding: 36px 40px;
  max-width: 420px; width: 90%; text-align: center;
  box-shadow: 0 24px 60px rgba(0,0,0,0.2);
  animation: ul-modal-in 0.2s ease;
}
@keyframes ul-modal-in {
  from { opacity: 0; transform: scale(0.93) translateY(10px); }
  to { opacity: 1; transform: none; }
}
.ul-modal-icon {
  width: 64px; height: 64px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 18px;
}
.ul-modal-icon-danger { background: rgba(234,84,85,0.1); color: #ea5455; }
.ul-modal-title { font-size: 20px; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin: 0 0 10px; }
.ul-modal-text { font-size: 14px; color: var(--dk-text-secondary, #6e7d8f); margin: 0 0 28px; line-height: 1.6; }
.ul-modal-text strong { color: var(--dk-text-primary, #0D3349); }
.ul-modal-actions { display: flex; gap: 12px; justify-content: center; }
.ul-btn-secondary {
  flex: 1; padding: 11px 0; border-radius: 10px;
  border: 1.5px solid var(--dk-border-input, #e8ecf0); background: var(--dk-bg-card, #fff);
  font-size: 14px; font-weight: 600; color: var(--dk-text-secondary, #6e7d8f); cursor: pointer;
  transition: all 0.15s;
}
.ul-btn-secondary:hover { border-color: var(--dk-text-primary); color: var(--dk-text-primary); }
.ul-btn-danger {
  flex: 1; padding: 11px 0; border-radius: 10px;
  border: none; background: #ea5455;
  font-size: 14px; font-weight: 600; color: #fff; cursor: pointer;
  transition: background 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.ul-btn-danger:hover { background: #d63637; }
.ul-spinner-sm {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4); border-top-color: var(--dk-bg-card, #fff);
  animation: ul-spin 0.6s linear infinite;
}

@media (max-width: 768px) {
  .ul-stats-row { grid-template-columns: repeat(2, 1fr); }
  .ul-filters-bar { flex-direction: column; align-items: flex-start; }
  .ul-filters-right { width: 100%; justify-content: space-between; }
  .ul-search-input { width: 100%; }
  .ul-page-header { flex-direction: column; align-items: flex-start; gap: 14px; }
}
</style>
